<template>
  <div
    ref="tabsRef"
    class="segmented-tabs"
    :style="tabsStyle"
    role="tablist"
    :aria-label="ariaLabel"
  >
    <span class="segmented-tabs__indicator" :style="indicatorStyle" aria-hidden="true" />

    <button
      v-for="tab in tabs"
      :key="tab.value"
      class="segmented-tabs__item"
      :class="{ 'is-active': isActive(tab) }"
      type="button"
      role="tab"
      :aria-selected="isActive(tab)"
      :aria-disabled="tab.disabled || undefined"
      :disabled="tab.disabled"
      @click="selectTab(tab)"
    >
      <slot name="tab" :tab="tab" :active="isActive(tab)">
        <SvgIcon
          v-if="tab.iconName"
          class="segmented-tabs__icon"
          :class="tab.iconClass"
          :name="tab.iconName"
        />
        <span class="segmented-tabs__label" :class="{ 'has-mobile-label': tab.mobileLabel }">
          {{ tab.label }}
        </span>
        <span v-if="tab.mobileLabel" class="segmented-tabs__mobile-label">
          {{ tab.mobileLabel }}
        </span>
      </slot>
    </button>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import type { CSSProperties } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import type { SegmentedTabItem, SegmentedTabValue } from '@/types/components';

  defineOptions({
    name: 'SegmentedTabs'
  });

  const props = withDefaults(
    defineProps<{
      modelValue: SegmentedTabValue;
      tabs: SegmentedTabItem[];
      ariaLabel?: string;
    }>(),
    {
      ariaLabel: 'Tabs'
    }
  );

  const emit = defineEmits<{
    'update:modelValue': [value: SegmentedTabValue];
    change: [value: SegmentedTabValue];
  }>();

  const tabsRef = ref<HTMLElement>();
  const indicatorStyle = ref<CSSProperties>({
    opacity: 0,
    transform: 'translateX(0)',
    width: '0px'
  });

  const tabsStyle = computed<CSSProperties>(
    () =>
      ({
        gridTemplateColumns: `repeat(${Math.max(props.tabs.length, 1)}, minmax(0, 1fr))`
      }) as CSSProperties
  );

  const activeIndex = computed(() => props.tabs.findIndex((tab) => tab.value === props.modelValue));

  let resizeObserver: ResizeObserver | undefined;

  const isActive = (tab: SegmentedTabItem) => tab.value === props.modelValue;

  const selectTab = (tab: SegmentedTabItem) => {
    if (tab.disabled || isActive(tab)) return;

    emit('update:modelValue', tab.value);
    emit('change', tab.value);
  };

  const syncIndicator = async () => {
    await nextTick();

    const tabButtons = tabsRef.value?.querySelectorAll<HTMLButtonElement>('.segmented-tabs__item');
    const activeButton = activeIndex.value >= 0 ? tabButtons?.[activeIndex.value] : undefined;

    if (!activeButton) {
      indicatorStyle.value = {
        opacity: 0,
        transform: 'translateX(0)',
        width: '0px'
      };
      return;
    }

    const nextStyle: CSSProperties = {
      opacity: 1,
      transform: `translateX(${activeButton.offsetLeft}px)`,
      width: `${activeButton.offsetWidth}px`
    };

    if (
      indicatorStyle.value.opacity === nextStyle.opacity &&
      indicatorStyle.value.transform === nextStyle.transform &&
      indicatorStyle.value.width === nextStyle.width
    ) {
      return;
    }

    indicatorStyle.value = nextStyle;
  };

  watch(
    () => [props.modelValue, props.tabs] as const,
    () => {
      void syncIndicator();
    },
    { deep: true }
  );

  onMounted(() => {
    void syncIndicator();

    if (typeof ResizeObserver === 'undefined' || !tabsRef.value) return;

    resizeObserver = new ResizeObserver(() => {
      void syncIndicator();
    });
    resizeObserver.observe(tabsRef.value);
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
  });
</script>

<style scoped>
  .segmented-tabs {
    --segmented-tabs-width: 472px;
    --segmented-tabs-height: 44px;
    --segmented-tabs-padding: 4px;
    --segmented-tabs-gap: 5px;
    --segmented-tabs-radius: 16px;
    --segmented-tabs-background: #f5f5f5;
    --segmented-tabs-indicator-radius: 12px;
    --segmented-tabs-indicator-background: #fff;
    --segmented-tabs-indicator-shadow: 0 8px 18px rgba(96, 51, 225, 0.08);
    --segmented-tabs-item-height: 36px;
    --segmented-tabs-item-radius: 12px;
    --segmented-tabs-text-color: #160211;
    --segmented-tabs-active-color: var(--segmented-tabs-text-color);
    --segmented-tabs-focus-color: #6033e1;
    --segmented-tabs-font-family:
      'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    --segmented-tabs-font-size: 14px;
    --segmented-tabs-font-weight: 400;
    --segmented-tabs-line-height: 14px;
    --segmented-tabs-item-padding: 10px;
    --segmented-tabs-icon-label-gap: 10px;

    position: relative;
    display: grid;
    width: var(--segmented-tabs-width);
    max-width: 100%;
    height: var(--segmented-tabs-height);
    box-sizing: border-box;
    padding: var(--segmented-tabs-padding);
    border-radius: var(--segmented-tabs-radius);
    background: var(--segmented-tabs-background);
    gap: var(--segmented-tabs-gap);
    flex-shrink: 0;
  }

  .segmented-tabs__indicator {
    position: absolute;
    z-index: 0;
    top: var(--segmented-tabs-padding);
    bottom: var(--segmented-tabs-padding);
    left: 0;
    border-radius: var(--segmented-tabs-indicator-radius);
    background: var(--segmented-tabs-indicator-background);
    box-shadow: var(--segmented-tabs-indicator-shadow);
    pointer-events: none;
    transition:
      transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
      width 0.28s cubic-bezier(0.22, 1, 0.36, 1),
      opacity 0.2s ease,
      box-shadow 0.28s ease;
  }

  .segmented-tabs__item {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: var(--segmented-tabs-item-height);
    padding: var(--segmented-tabs-item-padding);
    border: 0;
    border-radius: var(--segmented-tabs-item-radius);
    background: transparent;
    color: var(--segmented-tabs-text-color);
    cursor: pointer;
    font: inherit;
    font-family: var(--segmented-tabs-font-family);
    font-size: var(--segmented-tabs-font-size);
    font-weight: var(--segmented-tabs-font-weight);
    line-height: var(--segmented-tabs-line-height);
    transition:
      color 0.22s ease,
      opacity 0.16s ease;
    gap: var(--segmented-tabs-icon-label-gap);
    -webkit-tap-highlight-color: transparent;
  }

  .segmented-tabs__item:not(:disabled):active {
    opacity: 0.86;
  }

  .segmented-tabs__item:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .segmented-tabs__item.is-active {
    color: var(--segmented-tabs-active-color);
  }

  .segmented-tabs__item:focus-visible {
    outline: 1px solid var(--segmented-tabs-focus-color);
    outline-offset: 2px;
  }

  .segmented-tabs__icon,
  .segmented-tabs__label,
  .segmented-tabs__mobile-label {
    transition:
      transform 0.24s cubic-bezier(0.22, 1, 0.36, 1),
      color 0.22s ease;
  }

  .segmented-tabs__item.is-active .segmented-tabs__icon,
  .segmented-tabs__item.is-active .segmented-tabs__label,
  .segmented-tabs__item.is-active .segmented-tabs__mobile-label {
    transform: translateY(-1px);
  }

  .segmented-tabs__icon {
    flex: 0 0 auto;
  }

  .segmented-tabs__label {
    display: inline;
  }

  .segmented-tabs__mobile-label {
    display: none;
  }

  @media (max-width: 767px) {
    .segmented-tabs {
      width: var(--segmented-tabs-mobile-width, 100%);
      height: var(--segmented-tabs-mobile-height, 44px);
      padding: var(--segmented-tabs-mobile-padding, var(--segmented-tabs-padding));
      border-radius: var(--segmented-tabs-mobile-radius, var(--segmented-tabs-radius));
      background: var(--segmented-tabs-mobile-background, #eff0f7);
      gap: var(--segmented-tabs-mobile-gap, 0px);
    }

    .segmented-tabs__indicator {
      box-shadow: var(--segmented-tabs-mobile-indicator-shadow, 0 6px 16px rgba(22, 2, 17, 0.06));
    }

    .segmented-tabs__item {
      height: var(--segmented-tabs-mobile-item-height, var(--segmented-tabs-item-height));
      border-radius: var(--segmented-tabs-mobile-item-radius, var(--segmented-tabs-item-radius));
      color: var(--segmented-tabs-mobile-text-color, var(--segmented-tabs-text-color));
      font-family: var(
        --segmented-tabs-mobile-font-family,
        'PingFang SC',
        'Source Han Sans CN VF',
        'Noto Sans SC',
        sans-serif
      );
      font-size: var(--segmented-tabs-mobile-font-size, 13px);
      line-height: var(--segmented-tabs-mobile-line-height, var(--segmented-tabs-line-height));
      gap: var(--segmented-tabs-mobile-icon-label-gap, 0);
    }

    .segmented-tabs__item.is-active {
      color: var(
        --segmented-tabs-mobile-active-color,
        var(--segmented-tabs-mobile-text-color, var(--segmented-tabs-active-color))
      );
    }

    .segmented-tabs__icon {
      display: var(--segmented-tabs-mobile-icon-display, none);
    }

    .segmented-tabs__label.has-mobile-label {
      display: none;
    }

    .segmented-tabs__mobile-label {
      display: inline;
    }
  }
</style>
